// Shaping the dispute_management responses into what this UI reads. Kept out of
// services.js so that file stays a flat list of endpoints.

// Array-valued filters collapse to CSV so the Django side parses them with a single
// `.get(key)` rather than `getlist`.
export const toQuery = (params) => {
  const out = {}

  for (const [key, value] of Object.entries(params || {})) {
    if (value == null) continue

    if (Array.isArray(value)) {
      if (value.length === 0) continue
      out[key] = value.join(",")
    } else {
      out[key] = value
    }
  }

  return out
}

// Backend name -> the name this UI reads. The columns, filters and detail panes are
// written against the values on the right; the keys are what dispute_management's
// DisputeSerializer actually sends. Both end up on the row, so anything posted back
// still has the name the backend understands.
const FIELD_ALIASES = {
  customer_name: "customer_full_name",
  category: "reason_category",
  response_deadline: "due_by",
  filed_date: "dispute_created_at",
  updated: "updated_at",
  provider_dispute_id: "processor_dispute_id",
  stage: "cycle_stage",
  card_brand: "card_type",
  card_country: "country",
  issuer_bank: "issuing_bank",
  category_label: "reason_category_label",
  provider_charge_id: "processor_charge_id",
  provider_payment_intent_id: "processor_payment_intent_id",
  provider_order_id: "processor_order_id"
}

// Every dispute the API hands back passes through here. Statuses need no translation any
// more — the backend spells them exactly as this UI does — only the field names differ.
export const normalizeDispute = (row) => {
  if (!row) return row

  const out = { ...row }

  for (const [from, to] of Object.entries(FIELD_ALIASES)) {
    if (row[from] !== undefined && out[to] === undefined) out[to] = row[from]
  }

  return out
}

export const normalizeListResponse = (response) => {
  if (!response?.ok || !response?.data?.results) return response

  return {
    ...response,
    data: { ...response.data, results: response.data.results.map(normalizeDispute) }
  }
}

const splitName = (full) => {
  const parts = String(full || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  return {
    first: parts.slice(0, -1).join(" ") || parts[0] || "",
    last: parts.length > 1 ? parts.at(-1) : ""
  }
}

// The Enrichment tab is a flat form, while the backend keeps what it captured from the
// processor nested by block in `provider_evidence`. Everything below already reached us at
// ingestion (CHA-11245) — it was simply invisible to the form, which is why the tab opened
// at 0 filled fields on a dispute we hold plenty of data for. A value the merchant supplied
// always wins over one we derived.
//
// Reads the raw row, so the field names here are the backend's, not the aliases above.
const buildEnrichmentPrefill = (row) => {
  const evidence = row?.provider_evidence || {}
  const payment = evidence.payment || {}
  const billing = evidence.billing_address || {}
  const customer = evidence.customer || {}
  const threeDs = payment.three_d_secure || {}
  const name = splitName(row?.customer_name)

  const prefill = {
    billing_first_name: name.first,
    billing_last_name: name.last,
    billing_email_address: row?.customer_email,
    customer_phone: row?.customer_phone,
    billing_address_1: billing.line1,
    billing_address_2: billing.line2,
    billing_city: billing.city,
    billing_state: billing.state,
    billing_zip: billing.postal_code,
    billing_country: billing.country,

    payment_method_type: payment.wallet,
    card_issuing_country: row?.card_country,
    card_fingerprint: payment.fingerprint,
    billing_descriptor: payment.statement_descriptor,
    authorization_response_code: payment.authorization_code,

    avs_results_address_line: payment.avs_line1_check,
    avs_results_postal_code: payment.avs_postal_code_check,
    cvv_results: payment.cvc_check,
    three_ds_eci_code: threeDs.electronic_commerce_indicator,

    transaction_status: row?.transaction_status,
    transaction_timestamp: row?.transaction_date,
    count_of_previous_orders: customer.orders_count,
    account_creation_timestamp: customer.created_at
  }

  // An empty string is not an answer; leaving the key out keeps the field counted as unfilled.
  return Object.fromEntries(
    Object.entries(prefill).filter(([, v]) => v !== undefined && v !== null && v !== "")
  )
}

// The detail page reads an envelope — `detail.dispute` plus the evidence state beside it —
// while the endpoint returns the dispute flat with an `evidence` block. Wrapped here so the
// page keeps its shape. `latest_version` stays unset until the PDF half (CHA1-1451) can
// actually serve pages: pointing it at a document with no rendered pages would paint an
// empty viewer instead of the honest "no evidence yet" state.
export const normalizeDetail = (data) => ({
  ...data,
  dispute: normalizeDispute(data),
  evidence_status: data?.evidence?.status ?? null,
  enrichment_fields: { ...buildEnrichmentPrefill(data), ...(data?.enrichment_fields || {}) }
})
